import {
  Box,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactNode } from "react"
import type { z } from "zod"

import { InventoryLotStatusIcon } from "../../../components"
import type { InventoryLotWithPallet } from "../../../types"
import { formatInventoryLocation } from "../../../utils"

import { AerosNumericField } from "@/components/UiKit/AerosNumericField/AerosNumericField"
import { type ProductPackagingType } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"
import { calculateRemainingShelfLife, getProductPackagingTypeLabel } from "@/utils/product"
import { isNil } from "@/utils/utils"

const LOT_COLUMN_COUNT = 9
const INPUT_COLUMN_COUNT = 1
const DERIVED_COLUMN_COUNT = 1
const DELETE_COLUMN_COUNT = 1
const FOOTER_VALUE_COLUMN_COUNT = 2

interface AdjustmentSelectedLotsProps {
  selectedLots: InventoryLotWithPallet[]
  uomName: string
  testIdPrefix: string
  title: string
  emptyState: { title: string; description: string }
  inputColumn?: {
    header: string
    values: Record<string, number | null>
    createSchema: (params: { lotQuantity: number | null | undefined }) => z.ZodTypeAny
    renderHelperText?: (inputQuantity: number, lotId: string) => ReactNode
    onChange: (lotId: string, value: number | null) => void
  }
  derivedColumn?: {
    header: string
    render: (inputQuantity: number, lotQuantity: number, uomName: string) => ReactNode
  }
  footer?: {
    label: string
    render: () => ReactNode
  }
  onLotRemove: (lotId: string) => void
}

export const AdjustmentSelectedLots: FC<AdjustmentSelectedLotsProps> = ({
  selectedLots,
  uomName,
  testIdPrefix,
  title,
  emptyState,
  inputColumn,
  derivedColumn,
  footer,
  onLotRemove,
}) => {
  const columnCount =
    LOT_COLUMN_COUNT +
    (inputColumn ? INPUT_COLUMN_COUNT : 0) +
    (derivedColumn ? DERIVED_COLUMN_COUNT : 0) +
    DELETE_COLUMN_COUNT

  return (
    <Box className='flex min-h-0 flex-1 flex-col rounded-lg border border-gray-400 p-4'>
      <Typography variant='h6' className='mb-4 font-bold leading-10'>
        2. {title}
      </Typography>
      <TableContainer className='min-h-0 flex-1 overflow-auto'>
        <Table stickyHeader className='w-full table-auto'>
          <TableHead>
            <TableRow>
              <TableCell className='whitespace-nowrap'>Reference #</TableCell>
              <TableCell className='whitespace-nowrap'>Location</TableCell>
              <TableCell className='whitespace-nowrap'>Lot #</TableCell>
              <TableCell className='whitespace-nowrap'>Batch #</TableCell>
              <TableCell className='whitespace-nowrap'>Remaining Shelf Life</TableCell>
              <TableCell className='whitespace-nowrap'>Packaging</TableCell>
              <TableCell className='whitespace-nowrap'>Units</TableCell>
              <TableCell className='whitespace-nowrap'>Pkg UoM</TableCell>
              <TableCell className='whitespace-nowrap'>Current Lot UoM</TableCell>
              {inputColumn && (
                <TableCell className='whitespace-nowrap'>
                  {inputColumn.header}
                  <span className='text-red-600'>*</span>
                </TableCell>
              )}
              {derivedColumn && <TableCell className='whitespace-nowrap'>{derivedColumn.header}</TableCell>}
              <TableCell className='w-12' />
            </TableRow>
          </TableHead>

          <TableBody>
            {!selectedLots.length && (
              <TableRow>
                <TableCell colSpan={columnCount} className='py-8'>
                  <p className='flex flex-col items-center gap-y-2 text-center'>
                    <span className='font-semibold text-gray-700'>{emptyState.title}</span>
                    <span className='max-w-[340px]'>{emptyState.description}</span>
                  </p>
                </TableCell>
              </TableRow>
            )}

            {selectedLots.map((lot) => {
              const remainingShelfLife = calculateRemainingShelfLife(lot.manufacturingDate, lot.expirationDate)

              const inputQuantity = inputColumn ? (inputColumn.values[lot.rawInventoryId] ?? null) : null
              const inputSchema = inputColumn?.createSchema({ lotQuantity: lot.totalUnits })
              const validationResult =
                inputColumn && !isNil(inputQuantity) ? inputSchema?.safeParse(inputQuantity) : null
              const inputError = validationResult?.success === false ? validationResult.error.errors[0].message : null
              const inputHelperText =
                inputColumn && !isNil(inputQuantity)
                  ? inputColumn.renderHelperText?.(inputQuantity, lot.rawInventoryId)
                  : null
              const helperText = inputError ?? inputHelperText ?? undefined

              return (
                <TableRow key={lot.rawInventoryId} data-testid={`${testIdPrefix}.row-${lot.rawInventoryId}`}>
                  <TableCell>{lot.pallet.referenceNbr}</TableCell>
                  <TableCell>{formatInventoryLocation(lot.pallet.location)}</TableCell>
                  <TableCell>
                    <Box className='flex items-center gap-x-1'>
                      <InventoryLotStatusIcon qaStatus={lot.qaStatus} />
                      {lot.lotNbr}
                    </Box>
                  </TableCell>
                  <TableCell>{lot.batchNbr ?? ""}</TableCell>
                  <TableCell className='whitespace-nowrap'>
                    {isNil(remainingShelfLife) ? "" : `${remainingShelfLife} %`}
                  </TableCell>
                  <TableCell>{getProductPackagingTypeLabel(lot.packageDesc as ProductPackagingType)}</TableCell>
                  <TableCell>{lot.totalPackages}</TableCell>
                  <TableCell className='whitespace-nowrap'>
                    {isNil(lot.packageSize) ? "" : `${formatNumber(lot.packageSize)} ${uomName}`}
                  </TableCell>
                  <TableCell className='whitespace-nowrap'>
                    {isNil(lot.totalUnits) ? "" : `${formatNumber(lot.totalUnits)} ${uomName}`}
                  </TableCell>
                  {inputColumn && (
                    <TableCell>
                      <AerosNumericField
                        value={inputQuantity ?? null}
                        error={!!inputError}
                        helperText={helperText}
                        allowNegative={false}
                        decimalScale={2}
                        slotProps={{
                          input: { endAdornment: <InputAdornment position='end'>{uomName}</InputAdornment> },
                        }}
                        onChange={(value) => inputColumn.onChange(lot.rawInventoryId, value)}
                        className='w-[140px]'
                        data-testid={`${testIdPrefix}.quantity-field-${lot.rawInventoryId}`}
                      />
                    </TableCell>
                  )}
                  {derivedColumn && (
                    <TableCell>
                      {isNil(inputQuantity) || isNil(lot.totalUnits)
                        ? null
                        : derivedColumn.render(inputQuantity, lot.totalUnits, uomName)}
                    </TableCell>
                  )}
                  <TableCell>
                    <IconButton
                      size='small'
                      onClick={() => onLotRemove(lot.rawInventoryId)}
                      data-testid={`${testIdPrefix}.remove-button-${lot.rawInventoryId}`}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}

            {footer && !!selectedLots.length && (
              <TableRow className='bg-gray-100' data-testid={`${testIdPrefix}.footer-row`}>
                <TableCell colSpan={columnCount - FOOTER_VALUE_COLUMN_COUNT} className='font-medium'>
                  {footer.label}
                </TableCell>
                <TableCell colSpan={FOOTER_VALUE_COLUMN_COUNT} className='whitespace-nowrap font-medium'>
                  {footer.render()}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
